import { Menu } from 'antd';
import { Link } from 'react-router-dom';
import * as React from 'react';
import { FC, ReactNode, useMemo } from 'react';
import { MenuProps } from 'antd/lib/menu';
import SubMenu from 'antd/lib/menu/SubMenu';
import { MenuTheme } from 'antd/lib/menu/MenuContext';
import { useLocation } from 'react-router';
import styled from 'styled-components';

export interface MenuItem {
  key: string;
  icon: ReactNode;
  text: ReactNode;
  route?: string;
  children?: Omit<MenuItem, 'children'>[];
}

export type MenuItems = MenuItem[];

const getMenuSelectionProps = (
  currentRoute: string,
  menuItems: MenuItems,
  parentRouteKey?: string
): { selectedKeys?: string[]; defaultOpenKeys?: string[] } => {
  for (const item of menuItems) {
    if (item.route && currentRoute.startsWith(item.route)) {
      return {
        selectedKeys: [item.key],
        defaultOpenKeys: parentRouteKey ? [parentRouteKey] : [],
      };
    }
    if (item.children) {
      const childSelected = getMenuSelectionProps(
        currentRoute,
        item.children,
        item.key
      );

      if (childSelected.selectedKeys && childSelected.selectedKeys.length > 0) {
        return childSelected;
      }
    }
  }

  return {};
};

const MenuItem = styled(Menu.Item)`
  height: unset !important;
  min-height: 2.5rem;
  white-space: normal;
  display: flex;
  align-items: center;
  padding-top: 0.5rem;
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: baseline;
  padding: 0.5rem 0;
`;

const LinkIconContainer = styled.div`
  line-height: 1; // fixes ant inheritance
`;

const LinkLabel = styled.div`
  line-height: 1.5;
`;

export interface MenuPropsEx extends Omit<MenuProps, 'children'> {
  menuItems: MenuItems;
  theme?: MenuTheme;
  mode?: MenuProps['mode'];
  focusable?: boolean;
  routePrefix?: string;
}

export const MenuEx: FC<MenuPropsEx> = ({
  menuItems,
  routePrefix = '',
  ...menuProps
}) => {
  const { pathname } = useLocation();
  const selectionProps = useMemo(
    () =>
      getMenuSelectionProps(
        pathname.substr(routePrefix?.length ?? 0),
        menuItems
      ),
    [menuItems, pathname, routePrefix]
  );

  return (
    <Menu theme="light" mode="inline" {...menuProps} {...selectionProps}>
      {menuItems.map(({ children, ...item }) =>
        children ? (
          <SubMenu key={item.key} icon={item.icon} title={item.text}>
            {children.map((childItem) => (
              <MenuItem key={childItem.key}>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                <StyledLink to={routePrefix + childItem.route!}>
                  <LinkIconContainer>{childItem.icon}</LinkIconContainer>
                  <LinkLabel>{childItem.text}</LinkLabel>
                </StyledLink>
              </MenuItem>
            ))}
          </SubMenu>
        ) : (
          <MenuItem key={item.key}>
            {/*eslint-disable-next-line @typescript-eslint/no-non-null-assertion*/}
            <StyledLink to={routePrefix + item.route!}>
              <LinkIconContainer>{item.icon}</LinkIconContainer>
              <LinkLabel>{item.text}</LinkLabel>
            </StyledLink>
          </MenuItem>
        )
      )}
    </Menu>
  );
};
